import { CSSProperties, FC, useEffect, useState } from 'react';
import { Box, makeStyles, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import {
  PanelResizeHandle,
  PanelResizeHandleProps,
} from 'react-resizable-panels';
import { DragIcon, DragActiveIcon } from './icons';
import './styles.css';
import { useDockableGroupContext } from './WbDockableGroup';
import { getFocusVisibleStyle } from '../../styles';

type Props = PanelResizeHandleProps & {
  direction: WbDockableHandleDirection;
  size?: number | string;
  showIconOnHover?: boolean;
  divider?: boolean;
  spacing?: boolean;
  style?: CSSProperties;
};

type WbDockableHandleDirection = 'horizontal' | 'vertical';
type PanelSide = 'left' | 'right' | 'top' | 'bottom';

const HIDE_MOBILE_RESIZER_WHEN_PRIMARY_EXPANDED = true;

const useStyles = makeStyles(
  theme => ({
    handleOuter: {
      '&:focus-visible $handleHorizontal': {
        ...getFocusVisibleStyle(theme),
      },
      '&:focus-visible $handleVertical': {
        ...getFocusVisibleStyle(theme),
      },
    },
    handleHorizontal: {},
    handleVertical: {},
  }),
  { name: 'WbDockableResizeHandle' },
);

const WbDockableResizeHandleVertical: FC<Props> = ({
  size = 24,
  divider,
  style,
}) => {
  const classes = useStyles();
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const onMouseUp = () => {
      setActive(false);
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  return (
    <Box
      className={clsx(
        'handle-vertical',
        classes.handleVertical,
        active && 'handle-vertical-active',
        divider && 'handle-vertical-divider',
      )}
      style={{ height: size, ...style }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {active || hovered ? (
        <DragActiveIcon
          color="primary"
          style={{ transform: 'rotate(90deg)', fontSize: 32 }}
        />
      ) : (
        <DragIcon
          color="primary"
          style={{ transform: 'rotate(90deg)', fontSize: 32 }}
        />
      )}
    </Box>
  );
};

const WbDockableResizeHandleHorizontal: FC<Props> = ({
  size = 24,
  divider,
  style,
}) => {
  const classes = useStyles();
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const onMouseUp = () => {
      setActive(false);
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  return (
    <Box
      className={clsx(
        'handle-horizontal',
        classes.handleHorizontal,
        active && 'handle-horizontal-active',
        divider && 'handle-horizontal-divider',
      )}
      style={{ width: size, ...style }}
      onMouseDown={() => setActive(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {active || hovered ? (
        <DragActiveIcon color="primary" style={{ fontSize: 24 }} />
      ) : (
        <DragIcon color="primary" style={{ fontSize: 24 }} />
      )}
    </Box>
  );
};

const handleByDirection: Record<
  WbDockableHandleDirection,
  (props: Props) => JSX.Element
> = {
  vertical: props => <WbDockableResizeHandleVertical {...props} />,
  horizontal: props => <WbDockableResizeHandleHorizontal divider {...props} />,
};
export const WbDockableResizeHandle: FC<Props> = props => {
  const classes = useStyles();
  const theme = useTheme();
  const ctx = useDockableGroupContext();
  const isMobile = ctx?.isMobile ?? false;
  const expandOnResize = ctx?.expandOnResize ?? true;
  let spacingStyle: CSSProperties | undefined;
  if (props.spacing && !isMobile) {
    spacingStyle =
      props.direction === 'horizontal'
        ? { marginInline: theme.spacing(1) }
        : { marginBlock: theme.spacing(1) };
  }

  const isPrimaryExpandedOnMobile =
    isMobile && !(ctx?.isPrimaryCollapsed ?? false);
  const hideMobileResizeHandle =
    HIDE_MOBILE_RESIZER_WHEN_PRIMARY_EXPANDED && isPrimaryExpandedOnMobile;

  const isPrimaryPanelFullyExpanded = ctx?.isPrimaryFullyExpanded ?? false;
  const primaryIndex = ctx?.primaryIndex ?? 0;
  const dockDirection = ctx?.direction ?? 'horizontal';

  // With two panels, primary index 0 means start side (left/top), index 1 means end side (right/bottom).
  let panelSide: PanelSide = 'left';
  if (dockDirection === 'horizontal') {
    panelSide = primaryIndex > 0 ? 'right' : 'left';
  } else {
    panelSide = primaryIndex > 0 ? 'bottom' : 'top';
  }

  const getTransform = () => {
    if (props.direction === 'horizontal') {
      if (!isPrimaryPanelFullyExpanded) return 'translateX(-50%)';
      return panelSide === 'right' ? 'translateX(1%)' : 'translateX(-99%)';
    }
    if (!isPrimaryPanelFullyExpanded) return 'translateY(-50%)';
    return panelSide === 'bottom' ? 'translateY(1%)' : 'translateY(-99%)';
  };

  const transformValue = getTransform();

  const baseStyle = {
    '--primary-main': theme.palette.primary.main,
    '--grey-300': theme.palette.grey[300],
  } as CSSProperties;

  const handleDoubleClick = () => {
    ctx?.resetToDefaults();
  };

  if (hideMobileResizeHandle) {
    return null;
  }

  if (isMobile || !expandOnResize) {
    return (
      <div
        aria-hidden
        tabIndex={-1}
        className={clsx('handle-outer', classes.handleOuter)}
        style={{ ...baseStyle, ...spacingStyle, ...props.style }}
      >
        <div className={clsx(`handle-${props.direction}-disabled`)} />
      </div>
    );
  }

  return (
    <PanelResizeHandle
      {...props}
      style={{ ...baseStyle, ...spacingStyle, ...props.style }}
      className={clsx('handle-outer', classes.handleOuter)}
    >
      <div onDoubleClick={handleDoubleClick} style={{ display: 'contents' }}>
        {handleByDirection[props.direction]({
          ...props,
          size: props.size ?? 24,
          style: { transform: transformValue },
        })}
      </div>
    </PanelResizeHandle>
  );
};
